import DialogContent from '@mui/material/DialogContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Grid from '@mui/material/Grid2'
import { notificationAPI } from 'api/NotificationAPI'
import ModalPaper from 'Components/reusable/CaleoCustomComponents/ModalPaper'
import withSimpleModal from 'Components/reusable/HOC/withSimpleModal'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { useUser } from 'hooks'
import React, { useEffect, useState } from 'react'
import { IError } from 'types/error'
import ErrorOverlay from '../ErrorOverlay'
import { useTranslation } from 'react-i18next'
import { IOptoutSetting } from 'types/notificationInterfaces'

/** @notExported */
interface INotificationModalProps {
  /** Indicator for submitting data. */
  submitIndicator: boolean
  /** Function for closing modal. */
  onClose: () => void
  /** Function for resetting submit indicator. */
  resetSubmit: () => void
}

/**
 * Notification modal.
 *
 * @returns Modal for managing notification settings.
 * @notExported
 */
const NotificationModal: React.FC<INotificationModalProps> = ({ submitIndicator, onClose, resetSubmit }) => {
  const { t } = useTranslation()
  const { setSuccessNotificationPopup } = useNotificationPopup()
  const { user, setUpdate, features } = useUser()
  const [optoutNewAssignments, setOptoutNewAssignments] = useState<boolean>(true)
  const [networkNotifications, setNetworkNotifications] = useState<boolean>(true)
  /*const [optoutWeeklySummary, setOptoutWeeklySummary] = useState<boolean>(true)
  const [optoutMonthlySummary, setOptoutMonthlySummary] = useState<boolean>(true)*/
  const [optoutChatMessages, setOptoutChatMessages] = useState<boolean>(true)
  const [optoutAssignmentAddition, setOptoutAssignmentAddition] = useState<boolean>(true)
  const [optoutContactNewAssignmentNotification, setOptoutContactNewAssignmentNotification] = useState<boolean>(true)
  const [optoutNewRoleNote, setOptoutNewRoleNote] = useState<boolean>(true)
  const [optoutNewWorkOrderResponse, setOptoutNewWorkOrderResponse] = useState<boolean>(true)
  const [optoutProposalAccept, setOptoutProposalAccept] = useState<boolean>(true)
  const [optoutProposalAutoClose, setOptoutProposalAutoClose] = useState<boolean>(true)
  const [optoutProposalReject, setOptoutProposalReject] = useState<boolean>(true)
  const [optoutRoleProposalUpdate, setOptoutRoleProposalUpdate] = useState<boolean>(true)
  const [optoutWorkOrderAutoClose, setOptoutWorkOrderAutoClose] = useState<boolean>(true)
  const [optoutWorkOrderResponseReceived, setOptoutWorkOrderResponseReceived] = useState<boolean>(true)
  const [optoutWorkOrderResponseAccepted, setOptoutWorkOrderResponseAccepted] = useState<boolean>(true)
  const [optoutWorkOrderResponseRejected, setOptoutWorkOrderResponseRejected] = useState<boolean>(true)
  const [backendError, setBackendError] = useState<IError>()

  useEffect(() => {
    ;(async () => {
      const controller = new AbortController()

      if (user) {
        try {
          const settings = await notificationAPI.getNotificationSettings(user.id, controller)

          {
            for (const setting of settings) {
              if (setting.type === 'newAssignments') {
                setOptoutNewAssignments(false)
              }
              if (setting.type === 'networkNotifications') {
                setNetworkNotifications(false)
              }
              /*if (setting.type === 'weeklyAssignments') {
                setOptoutWeeklySummary(false)
              }
              if (setting.type === 'monthlyAssignments') {
                setOptoutMonthlySummary(false)
              }*/
              if (setting.type === 'chatMessages') {
                setOptoutChatMessages(false)
              }
              if (setting.type === 'assignmentAddition') {
                setOptoutAssignmentAddition(false)
              }
              if (setting.type === 'contactNewAssignmentNotification') {
                setOptoutContactNewAssignmentNotification(false)
              }
              if (setting.type === 'newRoleNote') {
                setOptoutNewRoleNote(false)
              }
              if (setting.type === 'newWorkOrderResponse') {
                setOptoutNewWorkOrderResponse(false)
              }
              if (setting.type === 'proposalAccept') {
                setOptoutProposalAccept(false)
              }
              if (setting.type === 'proposalAutoClose') {
                setOptoutProposalAutoClose(false)
              }
              if (setting.type === 'proposalReject') {
                setOptoutProposalReject(false)
              }
              if (setting.type === 'roleProposalUpdate') {
                setOptoutRoleProposalUpdate(false)
              }
              if (setting.type === 'workOrderAutoClose') {
                setOptoutWorkOrderAutoClose(false)
              }
              if (setting.type === 'workOrderResponseReceived') {
                setOptoutWorkOrderResponseReceived(false)
              }
              if (setting.type === 'workOrderResponseAccepted') {
                setOptoutWorkOrderResponseAccepted(false)
              }
              if (setting.type === 'workOrderResponseRejected') {
                setOptoutWorkOrderResponseRejected(false)
              }
            }
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      }

      return () => {
        controller.abort()
      }
    })()
  }, [user])

  useEffect(() => {
    ;(async () => {
      const controller = new AbortController()

      if (submitIndicator && user) {
        try {
          const newOptions: IOptoutSetting[] = []

          if (!optoutNewAssignments) {
            newOptions.push({ accountId: user.id, type: 'newAssignments' })
          }

          if (!networkNotifications) {
            newOptions.push({ accountId: user.id, type: 'networkNotifications' })
          }

          /*if (!optoutWeeklySummary) {
            newOptions.push({ accountId: user.id, type: 'weeklyAssignments' })
          }

          if (!optoutMonthlySummary) {
            newOptions.push({ accountId: user.id, type: 'monthlyAssignments' })
          }*/

          if (!optoutChatMessages) {
            newOptions.push({ accountId: user.id, type: 'chatMessages' })
          }

          if (!optoutAssignmentAddition) {
            newOptions.push({ accountId: user.id, type: 'assignmentAddition' })
          }

          if (!optoutContactNewAssignmentNotification) {
            newOptions.push({ accountId: user.id, type: 'contactNewAssignmentNotification' })
          }

          if (!optoutNewRoleNote) {
            newOptions.push({ accountId: user.id, type: 'newRoleNote' })
          }

          if (!optoutNewWorkOrderResponse) {
            newOptions.push({ accountId: user.id, type: 'newWorkOrderResponse' })
          }

          if (!optoutProposalAccept) {
            newOptions.push({ accountId: user.id, type: 'proposalAccept' })
          }

          if (!optoutProposalAutoClose) {
            newOptions.push({ accountId: user.id, type: 'proposalAutoClose' })
          }
          if (!optoutProposalReject) {
            newOptions.push({ accountId: user.id, type: 'proposalReject' })
          }

          if (!optoutRoleProposalUpdate) {
            newOptions.push({ accountId: user.id, type: 'roleProposalUpdate' })
          }

          if (!optoutWorkOrderAutoClose) {
            newOptions.push({ accountId: user.id, type: 'workOrderAutoClose' })
          }

          if (!optoutWorkOrderResponseReceived) {
            newOptions.push({ accountId: user.id, type: 'workOrderResponseReceived' })
          }

          if (!optoutWorkOrderResponseAccepted) {
            newOptions.push({ accountId: user.id, type: 'workOrderResponseAccepted' })
          }

          if (!optoutWorkOrderResponseRejected) {
            newOptions.push({ accountId: user.id, type: 'workOrderResponseRejected' })
          }

          await notificationAPI.putNotificationSettings(user.id, newOptions, controller)
          {
            onClose()
            setSuccessNotificationPopup()
            setUpdate(true)
          }
        } catch (error) {
          setBackendError(error as IError)
          resetSubmit()
        }
      }

      return () => {
        controller.abort()
      }
    })()
  }, [
    networkNotifications,
    onClose,
    optoutAssignmentAddition,
    optoutChatMessages,
    optoutContactNewAssignmentNotification,
    optoutNewAssignments,
    optoutNewRoleNote,
    optoutNewWorkOrderResponse,
    optoutProposalAccept,
    optoutProposalAutoClose,
    optoutProposalReject,
    optoutRoleProposalUpdate,
    optoutWorkOrderAutoClose,
    optoutWorkOrderResponseAccepted,
    optoutWorkOrderResponseReceived,
    optoutWorkOrderResponseRejected,
    resetSubmit,
    setSuccessNotificationPopup,
    setUpdate,
    submitIndicator,
    user,
  ])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  const changeMainSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setOptoutNewAssignments(true)
      setNetworkNotifications(true)
      /*setOptoutWeeklySummary(true)
      setOptoutMonthlySummary(true)*/
      if (features.includes('chat')) setOptoutChatMessages(true)
      setOptoutAssignmentAddition(true)
      setOptoutContactNewAssignmentNotification(true)
      setOptoutNewRoleNote(true)
      setOptoutNewWorkOrderResponse(true)
      setOptoutProposalAccept(true)
      setOptoutProposalAutoClose(true)
      setOptoutProposalReject(true)
      setOptoutRoleProposalUpdate(true)
      setOptoutWorkOrderAutoClose(true)
      setOptoutWorkOrderResponseReceived(true)
      setOptoutWorkOrderResponseAccepted(true)
      setOptoutWorkOrderResponseRejected(true)
    } else {
      setOptoutNewAssignments(false)
      setNetworkNotifications(false)
      /*setOptoutWeeklySummary(false)
      setOptoutMonthlySummary(false)*/
      if (features.includes('chat')) setOptoutChatMessages(false)
      setOptoutAssignmentAddition(false)
      setOptoutContactNewAssignmentNotification(false)
      setOptoutNewRoleNote(false)
      setOptoutNewWorkOrderResponse(false)
      setOptoutProposalAccept(false)
      setOptoutProposalAutoClose(false)
      setOptoutProposalReject(false)
      setOptoutRoleProposalUpdate(false)
      setOptoutWorkOrderAutoClose(false)
      setOptoutWorkOrderResponseReceived(false)
      setOptoutWorkOrderResponseAccepted(false)
      setOptoutWorkOrderResponseRejected(false)
    }
  }

  return (
    <DialogContent>
      <ModalPaper>
        <Grid container>
          <FormControlLabel
            control={
              <Switch
                checked={
                  optoutNewAssignments &&
                  optoutChatMessages &&
                  networkNotifications /*&& optoutWeeklySummary && optoutMonthlySummary*/
                }
                data-testid="switch-notifications-all"
                onChange={changeMainSwitch}
              />
            }
            label={t('notificationSettings.switch')}
          />
          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutNewAssignments}
                  data-testid="switch-notifications-new-assignments"
                  onChange={() => setOptoutNewAssignments(!optoutNewAssignments)}
                />
              }
              label={t('notificationSettings.new-assignments')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={networkNotifications}
                  data-testid="switch-notifications-network"
                  onChange={() => setNetworkNotifications(!networkNotifications)}
                />
              }
              label={t('notificationSettings.network-notifications')}
            />
          </Grid>

          {/*<Grid item pl={4}>
            <FormControlLabel
              control={
                <Switch checked={optoutWeeklySummary} onChange={() => setOptoutWeeklySummary(!optoutWeeklySummary)} />
              }
              label={t('notificationSettings.weekly-summary')}
            />
          </Grid>
          <Grid item pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutMonthlySummary}
                  onChange={() => setOptoutMonthlySummary(!optoutMonthlySummary)}
                />
              }
              label={t('notificationSettings.monthly-summary')}
            />
          </Grid>*/}

          {features.includes('chat') && (
            <Grid pl={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={optoutChatMessages}
                    data-testid="switch-notifications-chat"
                    onChange={() => setOptoutChatMessages(!optoutChatMessages)}
                  />
                }
                label={t('notificationSettings.chat-messages')}
              />
            </Grid>
          )}

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutAssignmentAddition}
                  data-testid="switch-notifications-assignment-addition"
                  onChange={() => setOptoutAssignmentAddition(!optoutAssignmentAddition)}
                />
              }
              label={t('notificationSettings.assignment-addition')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutContactNewAssignmentNotification}
                  data-testid="switch-notifications-contact-new-assignment"
                  onChange={() => setOptoutContactNewAssignmentNotification(!optoutContactNewAssignmentNotification)}
                />
              }
              label={t('notificationSettings.contact-new-assignment')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutNewRoleNote}
                  data-testid="switch-notifications-new-role-note"
                  onChange={() => setOptoutNewRoleNote(!optoutNewRoleNote)}
                />
              }
              label={t('notificationSettings.new-role-note')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutNewWorkOrderResponse}
                  data-testid="switch-notifications-new-work-order-response"
                  onChange={() => setOptoutNewWorkOrderResponse(!optoutNewWorkOrderResponse)}
                />
              }
              label={t('notificationSettings.new-work-order-response')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutProposalAccept}
                  data-testid="switch-notifications-proposal-accept"
                  onChange={() => setOptoutProposalAccept(!optoutProposalAccept)}
                />
              }
              label={t('notificationSettings.proposal-accept')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutProposalAutoClose}
                  data-testid="switch-notifications-proposal-auto-close"
                  onChange={() => setOptoutProposalAutoClose(!optoutProposalAutoClose)}
                />
              }
              label={t('notificationSettings.proposal-auto-close')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutProposalReject}
                  data-testid="switch-notifications-proposal-reject"
                  onChange={() => setOptoutProposalReject(!optoutProposalReject)}
                />
              }
              label={t('notificationSettings.proposal-reject')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutRoleProposalUpdate}
                  data-testid="switch-notifications-role-proposal-update"
                  onChange={() => setOptoutRoleProposalUpdate(!optoutRoleProposalUpdate)}
                />
              }
              label={t('notificationSettings.role-proposal-update')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutWorkOrderAutoClose}
                  data-testid="switch-notifications-work-order-auto-close"
                  onChange={() => setOptoutWorkOrderAutoClose(!optoutWorkOrderAutoClose)}
                />
              }
              label={t('notificationSettings.work-order-auto-close')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutWorkOrderResponseReceived}
                  data-testid="switch-notifications-work-order-response-received"
                  onChange={() => setOptoutWorkOrderResponseReceived(!optoutWorkOrderResponseReceived)}
                />
              }
              label={t('notificationSettings.work-order-response-received')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutWorkOrderResponseAccepted}
                  data-testid="switch-notifications-work-order-response-accepted"
                  onChange={() => setOptoutWorkOrderResponseAccepted(!optoutWorkOrderResponseAccepted)}
                />
              }
              label={t('notificationSettings.work-order-response-accepted')}
            />
          </Grid>

          <Grid pl={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={optoutWorkOrderResponseRejected}
                  data-testid="switch-notifications-work-order-response-rejected"
                  onChange={() => setOptoutWorkOrderResponseRejected(!optoutWorkOrderResponseRejected)}
                />
              }
              label={t('notificationSettings.work-order-response-rejected')}
            />
          </Grid>
        </Grid>
      </ModalPaper>
    </DialogContent>
  )
}

export default withSimpleModal(NotificationModal)
